import { type ColumnPinningState } from '@tanstack/react-table';
import { type RefObject, useEffect, useMemo, useState } from 'react';

import { type PinnedOffsets } from './dataTableCellStyles';
import { type DataTableColumnDef } from './types';

const DEFAULT_COLUMN_SIZE = 150;
const PIXEL_EPSILON = 0.5;

export type ColumnSizingInput = {
  id: string;
  size: number;
  minSize: number;
  maxSize: number;
  flex: number;
};

export type ColumnSizingResult = {
  /** Pixel width per column id, suitable for `<col style={{ width }}>`. */
  widths: Record<string, number>;
  /** Sum of allocated widths. The `<table>` should be set to this width. */
  totalWidth: number;
  /** Sum of `minSize` floors. The wrapper begins to scroll when below this. */
  minTotalWidth: number;
};

const sum = (values: readonly number[]) => values.reduce((acc, value) => acc + value, 0);

/**
 * Allocate per-column widths against a container width using a single-axis flex
 * algorithm:
 *
 *   1. If `container >= Σ size`: distribute the surplus to columns with `flex > 0`,
 *      proportional to weight, capped by `maxSize`. Spillover from capped columns
 *      cascades to the still-uncapped flex columns.
 *   2. If `Σ minSize <= container < Σ size`: shrink columns proportional to their
 *      shrinkable space (`size - minSize`) so widths sum to the container.
 *   3. If `container < Σ minSize`: lock widths at `minSize`. The wrapper takes over
 *      with horizontal scroll.
 *
 * After the layout pass, fractional widths are rounded down and the residual is
 * sprinkled across columns with the largest fractional remainders so the integer
 * total matches the unrounded total exactly.
 */
export const allocateColumnWidths = (
  inputs: readonly ColumnSizingInput[],
  containerWidth: number,
): ColumnSizingResult => {
  if (inputs.length === 0) {
    return { widths: {}, totalWidth: 0, minTotalWidth: 0 };
  }

  const sizes = inputs.map((input) => input.size);
  const mins = inputs.map((input) => input.minSize);
  const maxes = inputs.map((input) => input.maxSize);
  const grows = inputs.map((input) => input.flex);

  const baseTotal = sum(sizes);
  const minTotal = sum(mins);

  // Below the floor (or before the first measurement) we render at base sizes and let
  // the wrapper scroll. Mirrors the prior renderer's behavior on initial mount.
  if (containerWidth <= 0) {
    const widths = Object.fromEntries(inputs.map((input, index) => [input.id, sizes[index]]));
    return { widths, totalWidth: baseTotal, minTotalWidth: minTotal };
  }

  let widths: number[];

  if (containerWidth >= baseTotal) {
    widths = sizes.slice();
    let remaining = containerWidth - baseTotal;
    const capped = new Set<number>();

    while (remaining > PIXEL_EPSILON) {
      let activeGrowSum = 0;
      for (let i = 0; i < inputs.length; i += 1) {
        if (grows[i] > 0 && !capped.has(i) && widths[i] < maxes[i]) {
          activeGrowSum += grows[i];
        }
      }

      if (activeGrowSum === 0) {
        break;
      }

      let distributed = 0;
      let cappedThisPass = false;

      for (let i = 0; i < inputs.length; i += 1) {
        if (grows[i] === 0 || capped.has(i) || widths[i] >= maxes[i]) {
          continue;
        }

        const share = (remaining * grows[i]) / activeGrowSum;
        const target = widths[i] + share;

        if (target >= maxes[i]) {
          distributed += maxes[i] - widths[i];
          widths[i] = maxes[i];
          capped.add(i);
          cappedThisPass = true;
        } else {
          widths[i] = target;
          distributed += share;
        }
      }

      remaining -= distributed;
      if (!cappedThisPass) {
        break;
      }
    }
  } else if (containerWidth >= minTotal) {
    const shrinkSpaces = sizes.map((size, i) => Math.max(0, size - mins[i]));
    const totalShrinkSpace = sum(shrinkSpaces);
    const deficit = baseTotal - containerWidth;

    if (totalShrinkSpace <= 0) {
      widths = sizes.slice();
    } else {
      const ratio = Math.min(1, deficit / totalShrinkSpace);
      widths = sizes.map((size, i) => size - shrinkSpaces[i] * ratio);
    }
  } else {
    widths = mins.slice();
  }

  const floored = widths.map(Math.floor);
  const target = Math.round(sum(widths));
  const flooredTotal = sum(floored);
  let residual = Math.max(0, target - flooredTotal);

  if (residual > 0) {
    const fractionalOrder = widths
      .map((width, index) => ({ index, fraction: width - Math.floor(width) }))
      .sort((a, b) => b.fraction - a.fraction);

    let cursor = 0;
    let incrementsThisPass = 0;
    while (residual > 0 && fractionalOrder.length > 0) {
      const { index } = fractionalOrder[cursor % fractionalOrder.length];
      if (floored[index] + 1 <= maxes[index]) {
        floored[index] += 1;
        residual -= 1;
        incrementsThisPass += 1;
      }
      cursor += 1;

      if (cursor % fractionalOrder.length === 0) {
        if (incrementsThisPass === 0) {
          break;
        }
        incrementsThisPass = 0;
      }
    }
  }

  const widthsRecord: Record<string, number> = {};
  inputs.forEach((input, index) => {
    widthsRecord[input.id] = floored[index];
  });

  return {
    widths: widthsRecord,
    totalWidth: sum(floored),
    minTotalWidth: minTotal,
  };
};

const useContainerWidth = (ref: RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (element == null) {
      return;
    }

    const updateWidth = () => {
      setWidth((current) => {
        const next = element.clientWidth;
        return current === next ? current : next;
      });
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return width;
};

const getColumnSizingId = <TData extends object>(
  column: DataTableColumnDef<TData>,
  fallbackKey: string,
): string => {
  if (column.id != null) {
    return column.id;
  }
  if ('accessorKey' in column && column.accessorKey != null) {
    return String(column.accessorKey);
  }
  return fallbackKey;
};

const collectLeafSizingInputs = <TData extends object>(
  columns: readonly DataTableColumnDef<TData>[],
  parentKey: string,
  inputs: ColumnSizingInput[],
) => {
  columns.forEach((column, index) => {
    const id = getColumnSizingId(column, `${parentKey}-${index}`);

    const childColumns =
      'columns' in column && Array.isArray(column.columns)
        ? (column.columns as DataTableColumnDef<TData>[])
        : null;

    if (childColumns && childColumns.length > 0) {
      collectLeafSizingInputs(childColumns, id, inputs);
      return;
    }

    const declaredSize = column.size ?? DEFAULT_COLUMN_SIZE;
    const declaredMin = column.minSize ?? declaredSize;
    const declaredMax = column.maxSize ?? Number.POSITIVE_INFINITY;

    // Reconcile to `min <= size <= max`. If the user gave conflicting values, the
    // floor/ceiling win and `size` is clamped between them.
    const minSize = Math.min(declaredMin, declaredMax);
    const maxSize = Math.max(declaredMin, declaredMax);
    const size = Math.min(Math.max(declaredSize, minSize), maxSize);

    inputs.push({
      id,
      size,
      minSize,
      maxSize,
      flex: Math.max(column.flex ?? 0, 0),
    });
  });
};

/**
 * Walk a `DataTableColumnDef` tree (flat or grouped via `columns`) and return one
 * sizing input per leaf column.
 */
export const extractColumnSizingInputs = <TData extends object>(
  columns: readonly DataTableColumnDef<TData>[],
): ColumnSizingInput[] => {
  const inputs: ColumnSizingInput[] = [];
  collectLeafSizingInputs(columns, 'root', inputs);
  return inputs;
};

const useDataTableColumnWidths = <TData extends object>(
  scrollWrapperRef: RefObject<HTMLElement | null>,
  columns: readonly DataTableColumnDef<TData>[],
): ColumnSizingResult => {
  const containerWidth = useContainerWidth(scrollWrapperRef);

  const inputs = useMemo(() => extractColumnSizingInputs(columns), [columns]);

  return useMemo(() => allocateColumnWidths(inputs, containerWidth), [inputs, containerWidth]);
};

/**
 * Compute sticky offsets for every pinned leaf column directly from allocated widths.
 * Bypasses TanStack's `column.getStart`/`getAfter` (whose memo invalidation desyncs
 * from React commits when `state.columnSizing` is provided externally — see
 * `getPinnedCellStyles` notes).
 */
export const computePinnedOffsets = (
  pinning: ColumnPinningState | undefined,
  widths: Record<string, number>,
): PinnedOffsets => {
  const offsets: PinnedOffsets = {};
  const start = pinning?.start ?? [];
  const end = pinning?.end ?? [];

  let cumulativeLeft = 0;
  for (const id of start) {
    offsets[id] = { left: cumulativeLeft };
    cumulativeLeft += widths[id] ?? 0;
  }

  let cumulativeRight = 0;
  for (let i = end.length - 1; i >= 0; i -= 1) {
    const id = end[i];
    offsets[id] = { right: cumulativeRight };
    cumulativeRight += widths[id] ?? 0;
  }

  return offsets;
};

export default useDataTableColumnWidths;
